import { TextInput } from '@any-ui-react/core';
import { addDays } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';

import { HintGoogleWorkspacePlan } from '@/common/components/employees/shared/HintContractType';
import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { CompanySelectSingle } from '@/common/components/shared/select/company/CompanySelect';
import { ContractTypeSelectSingle } from '@/common/components/shared/select/contract-type/ContractTypeSelect';
import { ContractType } from '@/common/graphql';
import { useContractTypePersistence } from '@/common/hooks/employees/useContractTypePersistence';
import { type EmployeeAddFormDataType, FormAction } from '@/common/types';
import { DateUtils } from '@/common/utils';

interface Props {
  hiredDate: string | null;
  mode?: FormAction;
}

export const ContractInformationFormFields = ({ hiredDate, mode = FormAction.CREATING }: Props) => {
  const {
    control,
    formState: { errors },
    register,
    trigger,
    watch,
    setValue,
  } = useFormContext<EmployeeAddFormDataType>();

  const { data: allContractTypes } = useContractTypePersistence();

  return (
    <fieldset className="form-fieldset">
      <Controller
        control={control}
        name="contractType"
        render={({ field: { onChange, value } }) => (
          <div className="col-span-2 lg:col-span-1">
            <ContractTypeSelectSingle
              error={errors.contractType?.message}
              label="Contract Type"
              maxItemVisible={7}
              required={true}
              value={value}
              onChange={async nextValue => {
                const isContractor = nextValue?.value === ContractType.Contractor;
                const isIntern = nextValue?.value === ContractType.Intern;
                const isPartTime = nextValue?.value === ContractType.PartTime;
                const nonProbationContract = isContractor || isIntern || isPartTime;

                onChange(nextValue?.value ?? null, { shouldDirty: true });

                if (mode === FormAction.CREATING && watch('companyEmailAccount')?.length) {
                  trigger('companyEmailAccount');
                }

                if (isContractor) {
                  setValue('jobTitle', null);
                }

                if (nonProbationContract) {
                  setValue('needsProbationReview', false);
                }
              }}
            />
            <HintGoogleWorkspacePlan allContractTypes={allContractTypes} contractType={watch('contractType')} />
          </div>
        )}
      />

      <TextInput
        {...register('employeeCode')}
        autoComplete="off"
        className="col-span-2 lg:col-span-1"
        data-1p-ignore={true}
        error={errors.employeeCode?.message}
        label="Employee Code"
        placeholder="Enter Employee Code"
      />

      <Controller
        control={control}
        name="companyId"
        render={({ field: { onChange, ...field } }) => (
          <div className="col-span-2 lg:col-span-1">
            <CompanySelectSingle
              {...field}
              error={errors.companyId?.message}
              label="Company"
              placeholder="Select Company"
              required={true}
              variables={{ filterOnlyAccessible: true, enabled: mode === FormAction.CREATING || null }}
              onChange={option => {
                onChange(option?.value);
                if (option?.countryId) {
                  setValue('companyCountryId', option.countryId);
                }
                if (!option?.value) {
                  setValue('businessUnitId', null);
                  setValue('departmentId', null);
                }
              }}
            />
          </div>
        )}
      />

      <Controller
        control={control}
        name="contractEndDate"
        render={({ field: { value, onChange, ...field } }) => (
          <div className="col-span-2 lg:col-span-1">
            <DatePicker
              {...field}
              clearable={true}
              disabled={!hiredDate}
              error={errors.contractEndDate?.message}
              label="Latest Contract End Date"
              minDate={hiredDate ? addDays(new Date(hiredDate), 1) : DateUtils.dateAnyMindStarts}
              placeholder="yyyy-MM-dd"
              value={value ?? undefined}
              onChange={value => {
                onChange(DateUtils.formatDate(value));
              }}
            />
          </div>
        )}
      />
    </fieldset>
  );
};
